import type { Chart as ChartType } from "chart.js";
import { sampleProjectionForChart } from "../calc/chartSample";
import type { ProjectionRow } from "../shared/types";

let chart: ChartType | null = null;

async function loadChartJs() {
  const {
    Chart,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Legend,
    Tooltip,
    Filler,
  } = await import("chart.js");

  Chart.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Legend,
    Tooltip,
    Filler,
  );
  return Chart;
}

export async function renderBalanceBwzChart(
  canvas: HTMLCanvasElement,
  projection: ProjectionRow[],
): Promise<void> {
  const Chart = await loadChartJs();
  const sampled = sampleProjectionForChart(projection);
  const labels = sampled.map((r) => r.date);
  const balances = sampled.map((r) => r.balance);
  const bwzValues = sampled.map((r) => r.bwz);

  if (chart) {
    chart.destroy();
    chart = null;
  }

  chart = new Chart(canvas, {
    type: "line",
    data: {
      labels,
      datasets: [
        {
          label: "Guthaben (EUR)",
          data: balances,
          borderColor: "#0f766e",
          backgroundColor: "rgba(15, 118, 110, 0.12)",
          yAxisID: "yBalance",
          tension: 0.15,
          pointRadius: 0,
          borderWidth: 2,
        },
        {
          label: "Bewertungszahl (BWZ)",
          data: bwzValues,
          borderColor: "#b45309",
          backgroundColor: "rgba(180, 83, 9, 0.08)",
          yAxisID: "yBwz",
          tension: 0.15,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: {
          position: "top",
          labels: { usePointStyle: true, boxWidth: 8 },
        },
        tooltip: {
          callbacks: {
            label(ctx) {
              const value = ctx.parsed.y;
              if (value == null) return "";
              if (ctx.dataset.yAxisID === "yBalance") {
                return `Guthaben: ${value.toLocaleString("de-DE", { minimumFractionDigits: 2 })} EUR`;
              }
              return `BWZ: ${value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
            },
          },
        },
      },
      scales: {
        x: {
          title: { display: true, text: "Datum (Monatsende)" },
          ticks: { maxTicksLimit: 8 },
        },
        yBalance: {
          type: "linear",
          position: "left",
          title: { display: true, text: "Guthaben (EUR)" },
          grid: { color: "rgba(28, 25, 23, 0.08)" },
        },
        yBwz: {
          type: "linear",
          position: "right",
          title: { display: true, text: "Bewertungszahl (BWZ)" },
          grid: { drawOnChartArea: false },
        },
      },
    },
  });
}

export function destroyChart(): void {
  if (chart) {
    chart.destroy();
    chart = null;
  }
}
